/**
 * SKN-ON Volume Discount Handler
 * Auto-applies discount codes based on cart quantity
 * Targets: R20 Renew, N6 Calm, C28 Sculpt (excludes kits)
 */

(function() {
  'use strict';

  // Serum variant IDs (from REST API)
  const SERUM_VARIANT_IDS = [
    52072014741843, // R20 Renew
    52072014676307, // N6 Calm
    52072012710227  // C28 Sculpt
  ];

  // Discount code mapping (quantity -> code)
  const DISCOUNT_CODES = {
    3: '3SERUMS',
    2: '2SERUMS'
  };

  /**
   * Count eligible serum items in cart
   * @param {Object} cart - Cart object from /cart.js
   * @returns {number} Total quantity of eligible serums
   */
  function countEligibleSerums(cart) {
    if (!cart || !cart.items || !Array.isArray(cart.items)) {
      console.warn('[Volume Discount] Invalid cart data:', cart);
      return 0;
    }

    const count = cart.items.reduce((total, item) => {
      if (item && item.variant_id && SERUM_VARIANT_IDS.includes(item.variant_id)) {
        return total + (item.quantity || 0);
      }
      return total;
    }, 0);

    return count;
  }

  /**
   * Determine which discount code to apply based on quantity
   * @param {number} quantity - Total eligible serum quantity
   * @returns {string|null} Discount code or null
   */
  function getDiscountCode(quantity) {
    // Find highest applicable discount (codes sorted descending)
    // IMPORTANT: Object.entries() returns entries sorted by numeric keys in ASCENDING order!
    // So we need to sort them descending manually
    const entries = Object.entries(DISCOUNT_CODES).sort((a, b) => parseInt(b[0]) - parseInt(a[0]));

    for (const [minQty, code] of entries) {
      if (quantity >= parseInt(minQty)) {
        return code;
      }
    }
    return null;
  }

  /**
   * Check if discount is currently applied to cart
   * @param {Object} cart - Cart object from /cart.js
   * @param {string} code - Discount code to check
   * @returns {boolean}
   */
  function isDiscountApplied(cart, code) {
    // Check cart-level discounts
    if (cart.cart_level_discount_applications &&
        cart.cart_level_discount_applications.some(app => app.title === code)) {
      return true;
    }

    // Check line item discounts
    if (cart.items && cart.items.some(item =>
        item.discounts && item.discounts.some(discount => discount.title === code))) {
      return true;
    }

    return false;
  }

  /**
   * Apply discount code via Ajax Cart API
   * @param {string} code - Discount code to apply
   * @returns {Promise<Object>} Updated cart object
   */
  async function applyDiscountCode(code) {
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: code
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to apply discount: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Remove all discount codes
   * @returns {Promise<Object>} Updated cart object
   */
  async function removeDiscountCodes() {
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: ''
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove discounts: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Fetch current cart state
   * @returns {Promise<Object>} Cart object
   */
  async function fetchCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Track last applied discount to prevent infinite loops
  let lastAppliedDiscount = null;
  let applicationAttempts = 0;
  let isUpdating = false; // Prevent re-entrant calls

  /**
   * Main discount update logic
   * Checks cart, counts serums, applies appropriate discount
   */
  async function updateVolumeDiscount() {
    // Prevent re-entrant calls (stop infinite loops)
    if (isUpdating) {
      console.log('[Volume Discount] Already updating, skipping duplicate call');
      return;
    }

    // Check if customer manually removed discount
    if (sessionStorage.getItem('volume_discount_removed')) {
      console.log('[Volume Discount] Customer removed discount, skipping auto-apply');
      return;
    }

    isUpdating = true;
    try {
      // Fetch current cart
      const cart = await fetchCart();

      // Count eligible serums
      const serumCount = countEligibleSerums(cart);
      console.log(`[Volume Discount] Found ${serumCount} eligible serums in cart`);

      // Determine appropriate discount code
      const targetCode = getDiscountCode(serumCount);
      console.log(`[Volume Discount] Target code for ${serumCount} serums: ${targetCode}`);

      if (!targetCode) {
        // No discount applicable (0 serums)
        console.log('[Volume Discount] No discount applicable');
        // Remove any existing volume discounts
        const currentDiscounts = cart.cart_level_discount_applications || [];
        const hasVolumeDiscount = currentDiscounts.some(app =>
          Object.values(DISCOUNT_CODES).includes(app.title)
        );

        if (hasVolumeDiscount) {
          console.log('[Volume Discount] Removing existing volume discount');
          await removeDiscountCodes();

          // Set flag to reopen cart drawer after reload
          sessionStorage.setItem('reopen_cart_drawer', 'true');

          // Reload the page to refresh cart totals
          console.log('[Volume Discount] Reloading page to refresh cart after discount removal...');
          window.location.reload();
        }
        lastAppliedDiscount = null;
        applicationAttempts = 0;
        return;
      }

      // Prevent infinite loop - if we just applied this discount, don't reapply
      if (lastAppliedDiscount === targetCode) {
        applicationAttempts++;
        if (applicationAttempts > 2) {
          console.log(`[Volume Discount] Already attempted ${targetCode} multiple times, stopping`);
          return;
        }
      } else {
        applicationAttempts = 0;
      }

      // Check if correct discount is already applied
      if (isDiscountApplied(cart, targetCode)) {
        console.log(`[Volume Discount] Correct discount already applied: ${targetCode}`);
        lastAppliedDiscount = targetCode;
        applicationAttempts = 0;
        return;
      }

      // Apply the new discount
      console.log(`[Volume Discount] Applying discount: ${targetCode}`);
      lastAppliedDiscount = targetCode;
      await applyDiscountCode(targetCode);

      // Verify application
      const updatedCart = await fetchCart();
      if (isDiscountApplied(updatedCart, targetCode)) {
        console.log(`[Volume Discount] Successfully applied: ${targetCode}`);
        applicationAttempts = 0;

        // Set flag to reopen cart drawer after reload
        sessionStorage.setItem('reopen_cart_drawer', 'true');

        // Reload the page to refresh cart totals
        // This ensures all UI elements show the correct discounted prices
        console.log('[Volume Discount] Reloading page to refresh cart totals...');
        window.location.reload();

        // NOTE: We do NOT dispatch cart:update or cart:updated events here
        // to prevent infinite loops (this function listens for those events)
      } else {
        console.warn(`[Volume Discount] Discount ${targetCode} not visible in cart (will show at checkout)`);
      }

    } catch (error) {
      console.error('[Volume Discount] Error updating discount:', error);
    } finally {
      // Always release the lock
      isUpdating = false;
    }
  }

  /**
   * Initialize discount handler
   * Sets up event listeners and performs initial check
   */
  function init() {
    console.log('[Volume Discount] Initializing handler...');

    // Check if we need to reopen cart drawer after reload
    if (sessionStorage.getItem('reopen_cart_drawer') === 'true') {
      sessionStorage.removeItem('reopen_cart_drawer');
      console.log('[Volume Discount] Reopening cart drawer after reload...');

      // Wait for DOM to be ready, then open cart drawer
      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer-component, cart-drawer, [class*="cart-drawer"]');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          console.log('[Volume Discount] Cart drawer reopened successfully');
        } else if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
          console.log('[Volume Discount] Cart drawer reopened successfully (showDialog)');
        } else {
          console.warn('[Volume Discount] Could not find cart drawer element or open method');
        }
      }, 500); // Small delay to ensure cart is fully rendered
    }

    // Run on page load
    updateVolumeDiscount();

    // Listen for cart updates (theme uses 'cart:update' event)
    document.addEventListener('cart:update', function() {
      console.log('[Volume Discount] Cart update event detected');
      updateVolumeDiscount();
    });

    // Also listen for legacy 'cart:updated' event (backwards compatibility)
    document.addEventListener('cart:updated', function() {
      console.log('[Volume Discount] Cart updated event detected');
      updateVolumeDiscount();
    });

    // Listen for cart drawer open event
    document.addEventListener('cart-drawer:open', function() {
      console.log('[Volume Discount] Cart drawer opened, checking discounts');
      updateVolumeDiscount();
    });

    // Also listen for theme:cart:open event (alternative cart open event)
    document.addEventListener('theme:cart:open', function() {
      console.log('[Volume Discount] Theme cart open event detected');
      updateVolumeDiscount();
    });

    // Watch for cart drawer opening via DOM changes (fallback method)
    const cartDrawerObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const target = mutation.target;
          // Add null check to prevent "Cannot read properties of null (reading 'tagName')" error
          if (target && target.tagName &&
              (target.tagName === 'CART-DRAWER' || target.classList.contains('cart-drawer'))) {
            if (target.hasAttribute('open') || target.open) {
              console.log('[Volume Discount] Cart drawer opened (DOM observer)');
              updateVolumeDiscount();
            }
          }
        }
      });
    });

    // Observe cart drawer element when it exists
    const observeCartDrawer = function() {
      const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, [class*="cart-drawer"]');
      if (cartDrawer) {
        cartDrawerObserver.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ['open', 'class']
        });
        console.log('[Volume Discount] Observing cart drawer for open events');
      }
    };

    // Try to observe immediately, and retry after a delay if not found
    observeCartDrawer();
    setTimeout(observeCartDrawer, 1000);

    // Listen for cart quantity input changes (when +/- buttons update the quantity field)
    const observeCartQuantityChanges = function() {
      // Target both cart drawer and cart page quantity inputs
      const quantityInputs = document.querySelectorAll('input[name="updates[]"], input[name^="updates"]');

      quantityInputs.forEach(input => {
        // Listen for value changes
        input.addEventListener('change', function() {
          console.log('[Volume Discount] Cart quantity input changed, triggering discount update');
          setTimeout(updateVolumeDiscount, 800);
        });
      });

      if (quantityInputs.length > 0) {
        console.log(`[Volume Discount] Observing ${quantityInputs.length} quantity inputs for changes`);
      }
    };

    // Observe quantity inputs immediately and after cart updates
    observeCartQuantityChanges();
    setTimeout(observeCartQuantityChanges, 1000);

    // Re-observe when cart drawer opens or cart is updated
    document.addEventListener('cart:update', observeCartQuantityChanges);
    document.addEventListener('cart:updated', observeCartQuantityChanges);
    document.addEventListener('cart-drawer:open', observeCartQuantityChanges);

    // Listen for Shopify Ajax API cart changes
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];

      // Intercept cart API calls
      if (typeof url === 'string' &&
          (url.includes('/cart/add.js') ||
           url.includes('/cart/update.js') ||
           url.includes('/cart/change.js'))) {

        return originalFetch.apply(this, args).then(response => {
          // Clone response so we can read it
          const clonedResponse = response.clone();

          // Update discount after cart modification
          clonedResponse.json().then((cartData) => {
            // Use cart data from response instead of fetching again
            const serumCount = countEligibleSerums(cartData);
            const targetCode = getDiscountCode(serumCount);
            console.log(`[Volume Discount] Cart updated - ${serumCount} serums, target: ${targetCode}`);

            // Small delay to let Shopify process, then update
            setTimeout(updateVolumeDiscount, 500);
          }).catch(() => {});

          return response;
        });
      }

      return originalFetch.apply(this, args);
    };

    console.log('[Volume Discount] Handler initialized');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API for manual triggering
  window.SKNVolumeDiscount = {
    update: updateVolumeDiscount,
    remove: async function() {
      sessionStorage.setItem('volume_discount_removed', 'true');
      await removeDiscountCodes();
    },
    reset: function() {
      sessionStorage.removeItem('volume_discount_removed');
      updateVolumeDiscount();
    }
  };

})();
